import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import {
  dailyTimesheets, type DailyTimesheetItem, type DayCode,
} from '@/api/timesheets';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import DatePopover from '@/components/DatePopover';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

/**
 * Денний табель — допоміжний документ для збору даних за один день.
 * Автопідтягування співробітників (без масового заповнення полів — години/код
 * вводяться вручну). Місячний табель потім збирається з денних.
 */
interface Form {
  organization_id: string;
  document_number: string;
  day_date: string;     // YYYY-MM-DD
  comment: string;
  items: DailyTimesheetItem[];
}

const TODAY = new Date().toISOString().slice(0, 10);

const EMPTY: Form = {
  organization_id: '', document_number: '', day_date: TODAY, comment: '', items: [],
};

const CODE_LABELS: Record<DayCode, string> = {
  W: 'Робота', V: 'Відпустка', S: 'Лікарняний', A: 'Прогул', OFF: 'Вихідний',
};

export default function DailyTimesheetEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const [form, setForm] = useState<Form>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [staffOnly, setStaffOnly] = useState(true);
  const v = useFormValidation();
  const numberPreview = useNumberPreview('doc.daily_timesheets', form.organization_id, isNew);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const {
    availableOrgs, hasPermissionInAnyOrg, loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');

  const docQuery = useQuery({
    queryKey: ['daily-timesheet', id],
    queryFn: () => dailyTimesheets.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  const empQ = useQuery({
    queryKey: ['employees', 'daily-ts', form.organization_id, staffOnly],
    queryFn: () => refs.employees(form.organization_id, {
      ...(staffOnly ? { employment_type: 'STAFF' } : {}),
      active_only: true,
    }),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const first = availableOrgs?.[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: first });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        day_date: d.day_date,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number, employee_id: it.employee_id,
          day_code: it.day_code, worked_hours: it.worked_hours, comment: it.comment,
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const create = useMutation({
    mutationFn: () => dailyTimesheets.create({
      organization_id: form.organization_id,
      document_number: form.document_number || null,
      day_date: form.day_date,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['daily-timesheets'] });
      navigate(`/daily-timesheets/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => dailyTimesheets.update(id!, {
      document_number: form.document_number,
      day_date: form.day_date,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['daily-timesheet', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => dailyTimesheets.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['daily-timesheets'] });
      navigate('/timesheets');
    },
  });

  function onSave() {
    if (!v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['day_date', form.day_date, 'Вкажіть дату'],
    ])) return;
    isNew ? create.mutate() : update.mutate();
  }

  // Автопідтягування співробітників (БЕЗ масового заповнення — код OFF, 0 годин).
  function fillEmployees() {
    if (!empQ.data || empQ.data.length === 0) return;
    setForm((f) => ({
      ...f,
      items: empQ.data!.map((e, idx) => ({
        line_number: idx + 1, employee_id: e.id,
        day_code: 'W' as DayCode, worked_hours: 8, comment: null,
      })),
    }));
  }

  function setItem(i: number, patch: Partial<DailyTimesheetItem>) {
    setForm((f) => ({
      ...f, items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function addRow() {
    setForm((f) => ({
      ...f, items: [...f.items, {
        line_number: f.items.length + 1, employee_id: '',
        day_code: 'W' as DayCode, worked_hours: 8, comment: null,
      }],
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f, items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card"><div style={{ padding: '1.5rem', textAlign: 'center' }}>
        <div style={{ padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6, maxWidth: 600, margin: '1rem auto' }}>
          ⚠ Немає прав створювати табель у жодній з ваших організацій.
        </div>
        <button onClick={() => navigate('/timesheets')}>Назад</button>
      </div></div>
    );
  }

  // ч.884 (новий дизайн, Закон #10): «ще дій» не було; денний табель не
  // проводиться. Прогрес — обовʼязкові: організація, дата; рядки.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.day_date ? 1 : 0) },
    { id: 'items', title: 'Співробітники', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий денний табель' : `Денний табель № ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        {/* ч.641: канон DocumentToolbar (денний табель не проводиться). */}
        <DocumentToolbar
          onBackToList={() => navigate('/timesheets')}
          onSave={onSave}
          savePending={create.isPending || update.isPending}
          postable={false}
          isNew={isNew}
          onDelete={() => { confirm('Видалити денний табель?').then((ok) => { if (ok) remove.mutate(); }); }}
          entriesDoc={{ type: 'doc.daily_timesheets', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {v.banner()}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.daily_timesheets', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew}
            onChange={(e) => { setForm({ ...form, organization_id: e.target.value }); v.clear('organization_id'); }}
            style={v.inputStyle('organization_id')}>
            <option value="">—</option>
            {(isNew ? availableOrgs ?? [] : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {v.error('organization_id')}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа</label>
          <input value={form.document_number}
            placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover value={form.day_date}
            onChange={(d) => { setForm({ ...form, day_date: d }); v.clear('day_date'); }} />
          {v.error('day_date')}
        </div>
      </div>

      </DocBlock>

      {/* ч.884: дії заповнення — у заголовку блоку (Закон #7: без емодзі ⇧,
          з малої; недоступні — disabled з підказкою). */}
      <DocBlock index={2} title="Співробітники"
        actions={(<>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={!staffOnly}
              onChange={(e) => setStaffOnly(!e.target.checked)} />
            включно з ЦПУ
          </label>
          <button type="button" className="btn-doc" onClick={fillEmployees}
            disabled={!empQ.data || empQ.data.length === 0}
            title={!empQ.data || empQ.data.length === 0 ? 'Немає співробітників за вибіркою'
              : 'Додати рядки для всіх співробітників'}>
            підтягнути співробітників{empQ.data && empQ.data.length > 0 ? ` (${empQ.data.length})` : ''}
          </button>
          <button type="button" className="primary" onClick={addRow}>+ додати рядок</button>
        </>)}>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
        Введіть для кожного співробітника код дня (робота/відпустка/лікарняний/прогул/
        вихідний) і години. Денний табель збирається у місячний кнопкою «Зібрати з денних».
      </p>

      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 25 }}>#</th>
            <th>Співробітник</th>
            <th style={{ width: 150 }}>Код дня</th>
            <th style={{ width: 100 }}>Години</th>
            <th>Коментар</th>
            <th style={{ width: 35 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                <select value={it.employee_id}
                  onChange={(e) => setItem(i, { employee_id: e.target.value })}>
                  <option value="">—</option>
                  {empQ.data?.map((e) => (
                    <option key={e.id} value={e.id}>{e.name}{e.code ? ` — ${e.code}` : ''}</option>
                  ))}
                </select>
              </td>
              <td>
                <select value={it.day_code}
                  onChange={(e) => {
                    const c = e.target.value as DayCode;
                    setItem(i, { day_code: c, worked_hours: c === 'W' ? (Number(it.worked_hours) || 8) : 0 });
                  }}>
                  {(Object.keys(CODE_LABELS) as DayCode[]).map((c) => (
                    <option key={c} value={c}>{CODE_LABELS[c]}</option>
                  ))}
                </select>
              </td>
              <td>
                <input type="number" step="0.5" min={0} max={24}
                  value={it.worked_hours} disabled={it.day_code !== 'W'}
                  onChange={(e) => setItem(i, { worked_hours: e.target.value })}
                  style={{ textAlign: 'right' }} />
              </td>
              <td>
                <input value={it.comment ?? ''}
                  onChange={(e) => setItem(i, { comment: e.target.value })} />
              </td>
              <td>
                <button onClick={() => removeRow(i)} className="danger">×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </TchTable>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })} />
        </div>
        <div>
          {/* ч.884: вкладень у денного табеля раніше не було. */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.daily_timesheets" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>

      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
